import clsx from 'clsx';
import { useEffect, useMemo, useRef, useState } from 'react';
import "./ChatMessageInput.scss";
import "./PaymentCardModal.scss";
import {
  AttachmentPreviewList,
  useChannelStateContext,
  useComponentContext,
  useMessageInputContext,
} from 'stream-chat-react';
import { useDropzone } from 'react-dropzone';

import { ModernMessageInput } from './ModernMessageInput';

import type { StreamChatType } from '../types';
import GGModalWindow from '../../components/UtilityComponents/GGModalWindow/GGModalWindow';
import { PaymentCardInterface } from '../../Models/ActionCard';
import { ActionCardAPIHandler } from '../../services/NetworkClient/BackendAPIs/ActionCardAPIHandler';
import SnackBar, { SnackTypes } from '../../components/UtilityComponents/SnackBar/SnackBar';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../redux/store';
import { closeModal } from '../../redux/actionCard/paymentCard/paymentCardModalHandler'
import GGButton from '../../components/CommonComponents/GGButton/GGButton';
import { Box, Chip, CircularProgress, Tooltip, IconButton } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { convertToMilliseconds } from '../../utils/DateAndTimeUtils';
import { RupeesToPaiseConversion, formatCurrencyIndian } from '../../utils/MoneyConversionUtils';
import CustomMessageDeliveryHandler from '../../services/ChatManager/CustomMessageDeliveryHandler';
import { setCardMap } from '../../redux/actionCard/paymentCard/paymentCardsByChannelSlice';
import GGLoaderButton from '../../components/CommonComponents/GGLoaderButton/GGLoaderButton';
import { OrderSummaryInterface } from '../../Models/OrderSummary';
import { ErrorLogger } from '../../utils/Logger';

export const ChatMessageInput = () => {

  // Snackbar based status handling 
  const snackBarRef = useRef<{ showSnackbar(): any }>(null)
  const [snackBarParams, setSnackBarParams] = useState({
    message: "",
    state: SnackTypes.error
  });

  const showSnackbar = (message: string, state: SnackTypes) => {
    setSnackBarParams({ message: message, state: state })
    snackBarRef.current?.showSnackbar()
  }

  const { TypingIndicator } = useComponentContext();
  const [showTapcard, setShowTapcard] = useState(false);
  const [loading, setLoading] = useState(false);
  const [breakupLoading, setBreakupLoading] = useState(false);
  const [modalPage, setModalPage] = useState<number>(0);
  const [orderSummary, setOrderSummary] = useState<OrderSummaryInterface | null>(null);
  const [paymentCardDetails, setPaymentCardDetails] = useState<PaymentCardInterface>({
    amount: 0,
    deliverables: [],
    deliveryDurationEpoch: 0,
    revisionCharge: 0,
    noOfRevisions: 0,
    remarks: "Remarks: "
  });
  const { channel, acceptedFiles = [], multipleUploads } = useChannelStateContext<StreamChatType>();
  const { numberOfUploads, uploadNewFiles, maxFilesLeft, isUploadEnabled } =
    useMessageInputContext<StreamChatType>();

  // deliverables chip handling
  const [chips, setChips] = useState<string[]>([]);
  const [deliverInputValue, setDeliverInputValue] = useState<string>("");

  // input states (as strings) for better UX and placeholders
  const [amountInput, setAmountInput] = useState<string>("");
  const [daysInput, setDaysInput] = useState<string>("");
  const [hoursInput, setHoursInput] = useState<string>("");
  const [noOfRevisionsInput, setNoOfRevisionsInput] = useState<string>("");
  const [revisionChargeInput, setRevisionChargeInput] = useState<string>("");

  // validation state
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});

  const setFieldTouched = (field: string) => {
    setTouched(prev => ({ ...prev, [field]: true }));
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter" && deliverInputValue.trim()) {
      event.preventDefault();
      setChips([...chips, deliverInputValue.trim()]);
      setDeliverInputValue("");
    }
  };

  const handleDeliveryChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDeliverInputValue(event.target.value);
  };

  const handleDelete = (chipToDelete: string) => {
    setChips(chips.filter((chip) => chip !== chipToDelete));
  };

  const handleDuration = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    const sanitized = value.replace(/[^0-9]/g, "");
    if (name === "days") {
      setDaysInput(sanitized);
    } else if (name === "hours") {
      setHoursInput(sanitized);
    }
  };

  const handleNumericInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    const sanitized = value.replace(/[^0-9]/g, "");
    switch (name) {
      case "amount":
        setAmountInput(sanitized);
        setPaymentCardDetails(prev => ({ ...prev, amount: sanitized ? parseInt(sanitized) : 0 }));
        break;
      case "noOfRevisions":
        setNoOfRevisionsInput(sanitized);
        setPaymentCardDetails(prev => ({ ...prev, noOfRevisions: sanitized ? parseInt(sanitized) : 0 }));
        break;
      case "revisionCharge":
        setRevisionChargeInput(sanitized);
        setPaymentCardDetails(prev => ({ ...prev, revisionCharge: sanitized ? parseInt(sanitized) : 0 }));
        break;
      default:
        break;
    }
  };

  const accept = useMemo(
    () =>
      acceptedFiles.reduce<Record<string, Array<string>>>((mediaTypeMap, mediaType) => {
        mediaTypeMap[mediaType] ??= [];
        return mediaTypeMap;
      }, {}),
    [acceptedFiles],
  );

  const { getRootProps, isDragActive, isDragReject } = useDropzone({
    accept,
    disabled: !isUploadEnabled || maxFilesLeft === 0,
    multiple: multipleUploads,
    noClick: true,
    onDrop: uploadNewFiles,
  });

  // redux
  const isModalOpen = useSelector((state: RootState) => state.paymentCardModal.open)
  const dispatch = useDispatch();

  const handleTapcardOpenButtonClick = () => {
    resetPaymentCardDetails();
    setModalPage(0);
    setShowTapcard(true);
  }
  const handleTapcardCloseButtonClick = () => {
    resetPaymentCardDetails();
    setShowTapcard(false);
  }
  // const handlePaymentCardFieldChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
  //   const field = e.target.name;
  //   let fieldValue = e.target.value;
  //   const changedData = { ...paymentCardDetails, [field]: fieldValue } as PaymentCardInterface;
  //   setPaymentCardDetails(changedData);
  // }
  const resetPaymentCardDetails = () => {
    setPaymentCardDetails({
      amount: 0,
      deliverables: [],
      deliveryDurationEpoch: 0,
      revisionCharge: 0,
      noOfRevisions: 0,
      remarks: "Remarks: "
    })
    setChips([]);
    setAmountInput("");
    setDaysInput("");
    setHoursInput("");
    setNoOfRevisionsInput("");
    setRevisionChargeInput("");
    setTouched({});
    setErrors({});
    setOrderSummary(null);
  }

  const handlePaymentCardSubmit = () => {
    const numericDays = parseInt(daysInput || "0");
    const numericHours = parseInt(hoursInput || "0");
    const amount_ = RupeesToPaiseConversion(paymentCardDetails.amount);
    const revisionCharge_ = RupeesToPaiseConversion(paymentCardDetails.revisionCharge);
    const durationEpoch_ = convertToMilliseconds(numericDays, numericHours);
    const paymentData_ = {
      ...paymentCardDetails,
      amount: amount_,
      deliverables: [...chips],
      deliveryDurationEpoch: durationEpoch_,
      revisionCharge: revisionCharge_,
    };
    createOrderProposalActionCard(paymentData_);
  }

  const validateForm = () => {
    const errors: Record<string, string> = {};
    const numericAmount = parseInt(amountInput || "0");
    const numericDays = parseInt(daysInput || "0");
    const numericHours = parseInt(hoursInput || "0");
    const numericRevisions = parseInt(noOfRevisionsInput || "0");
    const numericRevisionCharge = parseInt(revisionChargeInput || "0");

    if (!amountInput || numericAmount <= 0) errors.amount = "Enter a valid amount (> 0)";
    if ((!daysInput && !hoursInput) || (numericDays === 0 && numericHours === 0)) errors.duration = "Set a valid duration";
    if (hoursInput && (numericHours < 0 || numericHours > 23)) errors.hours = "Hours must be between 0 and 23";
    if (noOfRevisionsInput === undefined || noOfRevisionsInput === "") {
      errors.noOfRevisions = "Enter number";
    } else if (numericRevisions < 0) {
      errors.noOfRevisions = "Revisions cannot be negative";
    }
    if (revisionChargeInput === undefined || revisionChargeInput === "") {
      errors.revisionCharge = "Enter amount in ₹";
    } else if (numericRevisionCharge < 0) {
      errors.revisionCharge = "Charge cannot be negative";
    }
    if (chips.length === 0) errors.deliverables = "Add at least one deliverable";

    return errors;
  };

  const handleModalPageChange = async () => {
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      // mark all as touched to show errors
      setTouched({ amount: true, days: true, hours: true, noOfRevisions: true, revisionCharge: true, deliverables: true });
      showSnackbar("Please fix the highlighted fields", SnackTypes.error);
      return;
    }
    setErrors({});
    // Prepare payment data for API call
    const numericDays = parseInt(daysInput || "0");
    const numericHours = parseInt(hoursInput || "0");
    const amount_ = RupeesToPaiseConversion(paymentCardDetails.amount);
    const revisionCharge_ = RupeesToPaiseConversion(paymentCardDetails.revisionCharge);
    const durationEpoch_ = convertToMilliseconds(numericDays, numericHours);
    const paymentData_ = {
      ...paymentCardDetails,
      amount: amount_,
      deliverables: [...chips],
      deliveryDurationEpoch: durationEpoch_,
      revisionCharge: revisionCharge_,
    };

    const buyerID = channel.id?.split("_")[0];
    const gigID = channel.id?.split("_")[1];
    const paymentCardData = {
      type: "order",
      orderDetails: { ...paymentData_ },
      buyerId: buyerID,
      gigId: gigID,
      channelId: channel.id,
      validTill: new Date().toISOString()
    };

    setBreakupLoading(true);
    try {
      const orderSummary = await ActionCardAPIHandler.shared.getActionCardProposalBreakup(paymentCardData);
      setOrderSummary(orderSummary);
      setModalPage(prev => prev + 1);
    } catch (error) {
      ErrorLogger.log(error, 'ChatMessageInput.getOrderBreakup');
      showSnackbar("Failed to load order breakup", SnackTypes.error);
    } finally {
      setBreakupLoading(false);
    }
  }

  const modalHandlePrev = () => {
    setModalPage(prev => prev - 1);
  }

  const closeTapcardUtil = () => {
    dispatch(closeModal());
  };

  const createOrderProposalActionCard = async (savedPaymentData: any) => {
    const channelId = channel.id ?? ""
    const buyerID = channel.id?.split("_")[0];
    const gigID = channel.id?.split("_")[1];
    const paymentCardData = {
      type: "order",
      orderDetails: { ...savedPaymentData },
      buyerId: buyerID,
      gigId: gigID,
      channelId: channel.id,
      validTill: new Date().toISOString()
    }
    setLoading(true)

    try {
      const createdActionCard = await ActionCardAPIHandler.shared.createActionCard(paymentCardData)

      // Fetching and setting up cardsList in redux state so that when we send the custom message we will have the action card data handy to power it.
      const actionCards = await ActionCardAPIHandler.shared.getActionCardsByChannelId(channelId);
      dispatch(setCardMap(actionCards));

      // Once we have updated list of actioncards we send it to streamchat as a custom message
      const messageDeliveryHandler = new CustomMessageDeliveryHandler(channelId);
      await messageDeliveryHandler.sendOrderPaymentCardCustomMessage(createdActionCard.id)
      showSnackbar("Order proposal sent", SnackTypes.success);
    } catch (error) {
      ErrorLogger.log(error, 'ChatMessageInput.createOrderProposalActionCard');
      showSnackbar(error as string, SnackTypes.error);
    } finally {
      closeTapcardUtil();
      setModalPage(0);
      setLoading(false);
    }
  }

  useEffect(() => {
    if (isModalOpen) {
      handleTapcardOpenButtonClick()
    } else {
      handleTapcardCloseButtonClick()
    }
    // eslint-disable-next-line
  }, [isModalOpen])

  const TapCardView = modalPage === 0 ? (
    <div className='paymentCardModal'>
      <div className="inputBox">
        <div className="inputHead">Amount</div>
        <input 
          type="text" 
          name="amount" 
          className={clsx('inputField', { 'inputError': (touched.amount || errors.amount) && (!!errors.amount) })}
          value={amountInput}
          onChange={handleNumericInput}
          onBlur={() => setFieldTouched('amount')}
          placeholder='Enter amount in ₹'
          inputMode="numeric"
        />
        {(touched.amount || errors.amount) && errors.amount && (
          <div className="errorText">{errors.amount}</div>
        )}
      </div>
      <div className="inputBox">
        <div className="inputHead">Time to complete work</div>
        <div className="durationBox">
          <label style={{ display: 'flex', alignItems: 'center', marginRight: "2rem" }}>
            <input 
              style={{ marginRight: "1rem" }} 
              type="text" 
              name="days" 
              className={clsx('inputField', 'deliveryDuration', { 'inputError': (touched.days || errors.duration) && (!!errors.duration) })}
              value={daysInput}
              onChange={handleDuration}
              onBlur={() => setFieldTouched('days')}
              placeholder='Days' 
              inputMode="numeric"
            />
            Days
          </label>
          <label style={{ display: 'flex', alignItems: 'center', marginRight: "2rem" }}>
            <input 
              style={{ marginRight: "1rem" }} 
              type="text" 
              name="hours" 
              className={clsx('inputField', 'deliveryDuration', { 'inputError': (touched.hours || errors.duration || errors.hours) && (!!errors.duration || !!errors.hours) })}
              value={hoursInput}
              onChange={handleDuration}
              onBlur={() => setFieldTouched('hours')}
              placeholder='Hours' 
              inputMode="numeric"
            />
            Hours
          </label>
        </div>
        {(touched.days || touched.hours || errors.duration) && errors.duration && (
          <div className="errorText">{errors.duration}</div>
        )}
        {(touched.hours || errors.hours) && errors.hours && (
          <div className="errorText">{errors.hours}</div>
        )}
      </div>
      <div className="inputBox">
        <div className="inputHead withHelp">
          <span>Free Revisions</span>
          <Tooltip 
            title="These are the number of times client can request revision free of cost (included in your deal) once the final work has been submitted by you until client accepts the final order"
            arrow
            placement="top"
          >
            <IconButton size="small" className="helpIcon" aria-label="Help: free revisions">
              <InfoOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </div>
        <input 
          type="text" 
          name="noOfRevisions" 
          className={clsx('inputField', { 'inputError': (touched.noOfRevisions || errors.noOfRevisions) && (!!errors.noOfRevisions) })}
          value={noOfRevisionsInput}
          onChange={handleNumericInput}
          onBlur={() => setFieldTouched('noOfRevisions')}
          placeholder='Enter Number Of Free Revisions' 
          inputMode="numeric"
        />
        {(touched.noOfRevisions || errors.noOfRevisions) && errors.noOfRevisions && (
          <div className="errorText">{errors.noOfRevisions}</div>
        )}
      </div>
      <div className="inputBox">
        <div className="inputHead withHelp">
          <span>Paid Revision Cost</span>
          <Tooltip 
            title="Once all the free revisions provided by you in this order are used by client, he/she can either Accept the order or request Paid Revision, which will be charged as per this amount"
            arrow
            placement="top"
          >
            <IconButton size="small" className="helpIcon" aria-label="Help: paid revision cost">
              <InfoOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </div>
        <input 
          type="text" 
          name="revisionCharge" 
          className={clsx('inputField', { 'inputError': (touched.revisionCharge || errors.revisionCharge) && (!!errors.revisionCharge) })}
          value={revisionChargeInput}
          onChange={handleNumericInput}
          onBlur={() => setFieldTouched('revisionCharge')}
          placeholder='Enter amount in ₹'
          inputMode="numeric" 
        />
        {(touched.revisionCharge || errors.revisionCharge) && errors.revisionCharge && (
          <div className="errorText">{errors.revisionCharge}</div>
        )}
      </div>
      <div className="warningContainer">
        <WarningAmberIcon className="warningIcon" />
        <span className="warningText">It is <b>NOT advisable</b> to keep either Free Revisions or Paid Revision Cost as ZERO</span>
      </div>
      <div className='inputBox'>
        <div className="inputHead">Deliverables</div>
        <input
          type="text"
          placeholder="Press Enter to add. Ex: PNG, JPG, TXT, Excel"
          value={deliverInputValue}
          onChange={handleDeliveryChange}
          onKeyDown={handleKeyDown}
          className='inputField deliverables'
        />
        {(touched.deliverables || errors.deliverables) && chips.length === 0 && (
          <div className="errorText">{errors.deliverables || 'Add at least one deliverable'}</div>
        )}
        <Box sx={{ display: "flex", flexWrap: "wrap", mt: 2, gap: 1 }}>
          {chips.map((chip, index) => (
            <Chip
              key={index}
              label={chip}
              onDelete={() => handleDelete(chip)}
              color="secondary"
            />
          ))}
        </Box>
      </div>
      <div className="buttonBox">
        <GGButton 
          onClick={handleModalPageChange} 
          fullWidth
          disabled={breakupLoading}
        >
          {breakupLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={16} />
              Loading...
            </Box>
          ) : 'Next'}
        </GGButton>
      </div>
    </div>
  ) : (
    <div className='paymentCardModalSummary'>
      {orderSummary ? (
        <>
          <div className="modalOrderDetails">
            <p className='heading'>Your Earnings Breakdown</p>
            <div className="orderData">
              <div className="orderCharges">
                <span className="chargeHead">Base Order Amount</span>
                <span className="chargeAmount positive">{formatCurrencyIndian(orderSummary.primaryTransaction.basePrice)}</span>
              </div>
              <div className="orderCharges">
                <span className="chargeHead">Platform Fee ({(orderSummary.primaryTransaction.sellerFinancials.platformFee.percentage * 100).toFixed(1)}%)</span>
                <span className="chargeAmount negative">- {formatCurrencyIndian(orderSummary.primaryTransaction.sellerFinancials.platformFee.actualFeeCharged)}</span>
              </div>
              <div className="orderCharges">
                <span className="chargeHead">GST on Platform Fee ({(orderSummary.primaryTransaction.sellerFinancials.gstDetails.ratePercentage * 100).toFixed(0)}%)</span>
                <span className="chargeAmount negative">- {formatCurrencyIndian(orderSummary.primaryTransaction.sellerFinancials.gstDetails.actualGstAmount)}</span>
              </div>
              {orderSummary.primaryTransaction.sellerFinancials.tcsDetails && orderSummary.primaryTransaction.sellerFinancials.tcsDetails.actualTcsAmount > 0 && (
                <div className="orderCharges">
                  <span className="chargeHead">TCS ({(orderSummary.primaryTransaction.sellerFinancials.tcsDetails.ratePercentage * 100).toFixed(1)}%)</span>
                  <span className="chargeAmount negative">- {formatCurrencyIndian(orderSummary.primaryTransaction.sellerFinancials.tcsDetails.actualTcsAmount)}</span>
                </div>
              )}
              <hr />
              <div className="orderCharges bold primary">
                <span className="chargeHead">You'll receive</span>
                <span className="chargeAmount positive">{formatCurrencyIndian(orderSummary.primaryTransaction.sellerFinancials.totalAmountPayable)}</span>
              </div>

              {paymentCardDetails.revisionCharge > 0 && orderSummary.revisionTransactions && orderSummary.revisionTransactions.length > 0 && (
                <>
                  <div className="revisionSection">
                    <p className="revisionHeading">Per Extra Revision Charges</p>
                    <div className="orderCharges">
                      <span className="chargeHead">Revision Amount</span>
                      <span className="chargeAmount positive">{formatCurrencyIndian(orderSummary.revisionTransactions[0].basePrice)}</span>
                    </div>
                    <div className="orderCharges">
                      <span className="chargeHead">Platform Fee ({(orderSummary.revisionTransactions[0].sellerFinancials.platformFee.percentage * 100).toFixed(1)}%)</span>
                      <span className="chargeAmount negative">- {formatCurrencyIndian(orderSummary.revisionTransactions[0].sellerFinancials.platformFee.actualFeeCharged)}</span>
                    </div>
                    <div className="orderCharges">
                      <span className="chargeHead">GST on Platform Fee ({(orderSummary.revisionTransactions[0].sellerFinancials.gstDetails.ratePercentage * 100).toFixed(0)}%)</span>
                      <span className="chargeAmount negative">- {formatCurrencyIndian(orderSummary.revisionTransactions[0].sellerFinancials.gstDetails.actualGstAmount)}</span>
                    </div>
                    {orderSummary.revisionTransactions[0].sellerFinancials.tcsDetails && orderSummary.revisionTransactions[0].sellerFinancials.tcsDetails.actualTcsAmount > 0 && (
                      <div className="orderCharges">
                        <span className="chargeHead">TCS ({(orderSummary.revisionTransactions[0].sellerFinancials.tcsDetails.ratePercentage * 100).toFixed(1)}%)</span>
                        <span className="chargeAmount negative">- {formatCurrencyIndian(orderSummary.revisionTransactions[0].sellerFinancials.tcsDetails.actualTcsAmount)}</span>
                      </div>
                    )}
                    <div className="orderCharges bold revision">
                      <span className="chargeHead">You'll receive per extra revision</span>
                      <span className="chargeAmount positive">{formatCurrencyIndian(orderSummary.revisionTransactions[0].sellerFinancials.totalAmountPayable)}</span>
                    </div>
                  </div>
                </>
              )}
            </div>
            <div className="adSection">
              <div className="adSectionData">
                <div className="adTitle">⭐ Premium Membership (Coming Soon)</div>
                <div className="adContent">
                  <div>Exclusive access to freelancing trainings</div>
                  <div>Live Community Discussion Sessions</div>
                  <div>Zero Commission Fee Deduction for Premium Members</div>
                  <div>AI Support for Gig Polishing to make your Gig Stand Out</div>
                  <div>Priority Customer Support & Faster Resolution</div>
                  <div>Advanced Analytics & Performance Insights</div>
                  <div>Tax Filing Assistance & GST Support for Indian Freelancers</div>
                  <div>Client Communication Templates & Negotiation Tools</div>
                  <div>Portfolio Showcase & Featured Listing Opportunities</div>
                  <div>Skill Development Courses & Certification Programs</div>
                  <div>Payment Protection & Escrow Services</div>
                  <div>24/7 Legal Consultation for Freelance Contracts</div>
                </div>
              </div>
            </div>
          </div>
          <div className="buttonBox">
            <GGButton onClick={modalHandlePrev} fullWidth>Back</GGButton>
            <GGLoaderButton 
              isLoading={loading} 
              onClick={handlePaymentCardSubmit} 
              variant='contained' 
              className='active'
              fullWidth
              sx={{
                width: "200px"
              }}
            > 
              Create Order
            </GGLoaderButton>
          </div>
        </>
      ) : (
        <div className="loadingContainer">
          <CircularProgress />
          <p>Loading order breakdown...</p>
        </div>
      )}
    </div>
  )

  return (
    <div {...getRootProps({ className: clsx(`team-message-input__wrapper`) })}>
      <SnackBar ref={snackBarRef} severity={snackBarParams.state} alertDuration={5000} message={snackBarParams.message} />
      <GGModalWindow childView={TapCardView} open={showTapcard} title='Create New Order' onCloseHandler={closeTapcardUtil} width='500px' />
      {isDragActive && (
        <div
          className={clsx('str-chat__dropzone-container', {
            'str-chat__dropzone-container--not-accepted': isDragReject,
          })}
        >
          {!isDragReject && <p>Drag your files here</p>}
          {isDragReject && <p>Some of the files will not be accepted</p>}
        </div>
      )}
      <div className='team-message-input__input'>
        {/* Attachment Preview */}
        {!!numberOfUploads && (
          <div className='team-message-input__attachments'>
            <AttachmentPreviewList />
          </div>
        )}

        {/* Modern Message Input */}
        <ModernMessageInput />
      </div>
      {TypingIndicator && <TypingIndicator />}
    </div>
  );
};